import type { ChipIntent } from '@/components/common/chip';
import { formatInAppTz } from '@/lib/date';

import type { AssigneeType, InstallationAddress, InstallationStatus } from './types';

/** Order Date & Time: the UTC wire instant on the application (Eastern) clock (matches Orders). */
export function formatInstallationDateTime(iso: string): string {
  return formatInAppTz(iso, 'D MMM YYYY | hh:mm A');
}

/** Joins the real backend's structured detail address into the single display line the drawer
 *  already renders. No `country` field exists on the wire shape, so the line ends at the zipcode. */
export function formatInstallationAddress(address: InstallationAddress): string {
  return [
    [address.line1, address.line2].filter(Boolean).join(', '),
    address.city,
    `${address.state} ${address.zipcode}`,
  ]
    .filter(Boolean)
    .join(', ');
}

/** Latitude/longitude label value, fixed to 5 dp. */
export function formatCoordinates(lat: number, lon: number): string {
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

/** Labeled lat/lon for the Selected Device tooltip (Figma 8589:825488): "Lat: X | Lon: Y". */
export function formatLatLon(lat: number, lon: number): string {
  return `Lat: ${lat.toFixed(5)} | Lon: ${lon.toFixed(5)}`;
}

export function formatAssigneeType(type: AssigneeType): string {
  return type === 'internal' ? 'Internal' : 'External';
}

/** Strips the external-assignee "Employee Name" field down to letters + spaces as the user
 *  types (defense-in-depth alongside the zod regex — keeps digits/punctuation from ever
 *  landing in the field). */
export const stripToAlpha = (s: string): string => s.replace(/[^A-Za-z ]/g, '');

/** Status label + Figma colorway (ADR-0018): Open=blue, In Progress=yellow, Rejected=red,
 *  Completed=green. Keyed on the wire enum.
 *
 *  In Progress is the `review` colorway (`#FDDC69` / `#684E00`), read off the chip instance in
 *  Figma `Admin-Application-Design` node `30:398896`, whose text binds the `Default/Yellow/70`
 *  variable. It shipped as `wip` (`#FFD9BE` / `#8A3800`, a peach) — ADO 119717 #3. */
export const INSTALLATION_STATUS_CHIP: Record<
  InstallationStatus,
  { label: string; intent: ChipIntent }
> = {
  open: { label: 'Open', intent: 'published' },
  in_progress: { label: 'In Progress', intent: 'review' },
  rejected: { label: 'Rejected', intent: 'attention' },
  completed: { label: 'Completed', intent: 'approved' },
};
